import { ApiError } from '@/api/client';
import { useUpdatePlan } from '@/api/queries';
import type { EventCard } from '@/api/types';
import { Col, Form, Row, TextField } from '@/components/kit/Field';
import { MetricRow } from '@/components/kit/MetricRow';
import { Alert, Note } from '@/components/kit/Note';
import { Panel, Signals } from '@/components/kit/Panel';
import { useToast } from '@/components/kit/Toast';
import { money, num, percent, teams as teamsPlural } from '@/lib/format';

/**
 * Вкладка «Планирование».
 *
 * Пять чисел на мероприятие. План не раскладывается по номинациям —
 * заранее это неизвестно, решают студии. И план не равен лимиту:
 * лимит это потолок, за которым включается резерв, план —
 * реалистичное ожидание. Он ничего не блокирует.
 */
export function PlanTab({ event }: { event: EventCard }) {
  const update = useUpdatePlan(event.id);
  const toast = useToast();

  const plan = event.plan ?? {
    teams: 0,
    people: 0,
    revenueParticipation: 0,
    revenueTickets: 0,
    revenueClasses: 0,
    revenueTotal: 0,
    averageCheck: null,
  };

  const limit = event.computed.teamLimitTotal;

  const save = (patch: Partial<typeof plan>) =>
    update.mutate(
      {
        teams: plan.teams,
        people: plan.people,
        revenueParticipation: plan.revenueParticipation,
        revenueTickets: plan.revenueTickets,
        revenueClasses: plan.revenueClasses,
        ...patch,
      },
      {
        onSuccess: () => toast.show('План сохранён'),
        onError: (e) => toast.showError(e instanceof ApiError ? e.message : 'Не сохранилось'),
      },
    );

  const numberField = (
    label: string,
    value: number,
    field: keyof typeof plan,
    formatter: (v: number) => string,
  ) => (
    <TextField
      label={label}
      key={`${String(field)}-${value}`}
      defaultValue={formatter(value)}
      onBlur={(e) => {
        const next = Number(e.target.value.replace(/[^\d]/g, '')) || 0;
        if (next !== value) save({ [field]: next } as Partial<typeof plan>);
      }}
    />
  );

  return (
    <>
      <Signals>
        {plan.teams > limit ? (
          <Alert>
            <b>План по командам выше суммы лимитов.</b>
            <span>
              Лимиты номинаций дают {teamsPlural(limit)}, план {num(plan.teams)} — столько
              не поместится, сверх лимита команды уйдут в резерв
            </span>
          </Alert>
        ) : null}
      </Signals>

      <Panel title="План мероприятия">
        {/* Раздел карточки, а не экран-отчёт: цифры — срезом, без героев (кит 2.11) */}
        <MetricRow
          variant="slice"
          items={[
            {
              label: 'Выручка всего',
              value: money(plan.revenueTotal),
              dim: !plan.revenueTotal,
              sub: (
                <>
                  участие {money(plan.revenueParticipation)} · билеты {money(plan.revenueTickets)} ·
                  классы {money(plan.revenueClasses)}
                </>
              ),
            },
            {
              label: 'Команд',
              value: num(plan.teams),
              dim: !plan.teams,
              sub: (
                <>
                  лимиты дают <span className="num">{num(limit)}</span> · план{' '}
                  {percent(limit ? Math.round((plan.teams / limit) * 100) : 0)} от потолка
                </>
              ),
            },
            {
              label: 'Участников',
              value: num(plan.people),
              dim: !plan.people,
              sub: (
                <>
                  средний чек за участие{' '}
                  <span className="num">{plan.averageCheck ? money(plan.averageCheck) : '—'}</span>
                </>
              ),
            },
          ]}
        />

        <Form>
          <Row>
            <Col>{numberField('Команд, шт', plan.teams, 'teams', num)}</Col>
            <Col>{numberField('Участников, шт', plan.people, 'people', num)}</Col>
          </Row>
          {numberField('Выручка за участие', plan.revenueParticipation, 'revenueParticipation', money)}
          {numberField('Выручка за билеты зрителей', plan.revenueTickets, 'revenueTickets', money)}
          {/* Мерч план пока не учитывает — он появится, когда будет модуль (БЛ §3.8) */}
          {numberField('Выручка за мастер-классы', plan.revenueClasses, 'revenueClasses', money)}
        </Form>

        <Note>
          <b>План — не лимит.</b> Лимит это потолок номинации, за которым включается резерв. План
          ничего не блокирует: по нему отдел продаж ставит цель, а производство прикидывает нагрузку
          на площадку. Факт против плана — на дашборде и в отчётах
        </Note>
      </Panel>
    </>
  );
}
